<script setup>
import { nextTick, onMounted, reactive, ref, computed } from "vue";
import {
  notificationList,
  deleteNotification,
} from "@/api/notificationChannel";
import AddChannel from "@/views/components/AddChannel.vue";
import { parseTime } from "@/utils/ruoyi";
import { ElMessage } from "element-plus";
import zhCn from "element-plus/dist/locale/zh-cn.mjs";
import en from "element-plus/dist/locale/en.mjs";
import trTr from "element-plus/dist/locale/tr.mjs";
import ru from "element-plus/dist/locale/ru.mjs";

const { locale } = useI18n();
const language = ref(locale);
const locales = computed(() => {
  switch (language.value) {
    case "zh-cn":
      return zhCn;
    case "en":
      return en;
    case "tr-tr":
      return trTr;
    case "ru-ru":
      return ru;
    default:
      return en; // 默认返回英语
  }
});
// 初始化表单数据
const formInline = reactive({
  noticeName: "", // 通知通道名称
  noticeMode: "", // 通知方式
  pageSize: 10, // 每页显示条数
  pageNo: 1, // 当前页码
});

const background = ref(true);
const disabled = ref(false);

import { useI18n } from "vue-i18n";

// 引入i18n用于国际化
const { t } = useI18n();

const channel = ref(null); // 子组件 AddChannel 的引用
const tableData = ref(null); // 表格数据
const dialogVisible = ref(false); // 弹窗显示控制
const currentId = ref(null); // 当前编辑的通知通道 ID

// 获取通知通道列表
const getNotificationList = (params) => {
  notificationList(params).then((res) => {
    if (res && res.data) {
      tableData.value = res.data;
    }
  });
};

// 查询按钮点击事件
const onSubmit = () => {
  getNotificationList(formInline);
};

// 编辑按钮点击事件，打开弹窗并加载详情数据
const edit = async (row) => {
  currentId.value = row.id;
  dialogVisible.value = true;

  // 等待 DOM 更新后调用子组件的方法获取详情
  await nextTick();
  channel.value?.getDetail();
};

const deleteRow = (row) => {
  deleteNotification(row.id)
    .then(() => {
      ElMessage({
        message: t("group.successDelete"),
        type: "success",
      });
      getNotificationList(formInline);
    })
    .catch((err) => {
      ElMessage({
        message: err,
        type: "error",
      });
    });
};

// 新增按钮点击事件，打开弹窗并重置数据
const addChannel = () => {
  currentId.value = null;
  dialogVisible.value = true;
  channel.value?.addNotification();
};

// 分页控件 - 每页条数变化
const handleSizeChange = (val) => {
  formInline.pageSize = val;
  getNotificationList(formInline);
};

// 分页控件 - 当前页变化
const handleCurrentChange = (val) => {
  formInline.pageNo = val;
  getNotificationList(formInline);
};

// 关闭弹窗
const handleClose = () => {
  dialogVisible.value = false;
};

// 弹窗内取消操作，关闭弹窗并刷新列表
const submitCancel = () => {
  dialogVisible.value = false;
  getNotificationList(formInline);
};

// 组件挂载时获取通知通道列表
onMounted(() => {
  getNotificationList(formInline);
});
</script>

<template>
  <div class="container-fluid">
    <el-dialog
      v-model="dialogVisible"
      :title="currentId ? $t('alarm.edit') : $t('alarm.add')"
      width="60%"
      :before-close="handleClose"
    >
      <!-- 引入子组件 AddChannel，传入当前 ID，监听提交取消事件 -->
      <AddChannel
        ref="channel"
        :id="currentId"
        @submitCancel="submitCancel"
      ></AddChannel>
    </el-dialog>

    <div class="card">
      <div class="card-header">
        <h5>{{ $t("routesMenu.notification") }}</h5>
      </div>
      <div class="card-body">
        <!-- 查询条件表单 -->
        <el-form :inline="true" :model="formInline" class="demo-form-inline">
          <el-form-item :label="$t('notification.notificationChannelName')">
            <el-input
              v-model="formInline.noticeName"
              :placeholder="$t('notification.notificationChannelName')"
              clearable
            />
          </el-form-item>
          <el-form-item :label="$t('notification.notificationWay')">
            <el-input
              v-model="formInline.noticeMode"
              :placeholder="$t('notification.notificationWay')"
              clearable
            />
          </el-form-item>
          <el-form-item>
            <el-button type="primary" @click="onSubmit">{{
              $t("arg.query")
            }}</el-button>
            <el-button type="success" @click="addChannel">{{
              $t("alarm.add")
            }}</el-button>
          </el-form-item>
        </el-form>

        <!-- 通知通道表格 -->
        <div v-if="tableData && tableData.list">
          <el-config-provider :locale="locales">
            <el-table :data="tableData.list" style="width: 100%">
              <el-table-column
                prop="noticeName"
                :label="$t('notification.notificationChannelName')"
              />
              <el-table-column
                prop="noticeMode"
                :label="$t('notification.notificationWay')"
              />
              <el-table-column prop="createTime" :label="$t('home.createTime')">
                <template #default="scope">
                  {{ parseTime(scope.row.createTime) }}
                </template>
              </el-table-column>
              <el-table-column :label="t('device.operation')">
                <template #default="scope">
                  <el-link type="info" @click="edit(scope.row)">{{
                    $t("group.edit")
                  }}</el-link>

                  <el-popconfirm
                    confirm-button-text="Yes"
                    cancel-button-text="No"
                    :title="$t('notification.sureDelete')"
                    @confirm="deleteRow(scope.row)"
                  >
                    <template #reference>
                      <el-link type="warning" class="m-2">{{
                        $t("alarm.delete")
                      }}</el-link>
                    </template>
                  </el-popconfirm>
                </template>
              </el-table-column>
            </el-table>
          </el-config-provider>

          <!-- 分页 -->
          <div class="pagination">
            <el-pagination
              v-model:current-page="formInline.pageNo"
              v-model:page-size="formInline.pageSize"
              :page-sizes="[10, 100, 200, 300, 400]"
              :total="tableData.total"
              :disabled="disabled"
              :background="background"
              layout="sizes, prev, pager, next"
              @size-change="handleSizeChange"
              @current-change="handleCurrentChange"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
::v-deep .demo-form-inline .el-form-item__label {
  line-height: 39px !important;
}

.demo-form-inline .el-input {
  width: 220px;
}

.demo-form-inline .el-select {
  width: 220px;
}

.pagination {
  padding: 1rem 0;
  display: flex;
  justify-content: flex-end;
}
</style>
